import React, { JSX } from 'react';
import { ActionTableMain } from './actionTable/ATMain';
import { ActionTableFilterBar } from './actionTable/ATFilterBar';
import {
  ATContextProvider,
  ATContextProviderProps,
  ATColumnType,
  ATColumnConfigType,
  ATColumnConfigOptionsType,
  ATFilterItemsType,
  useATColumns,
  useATFilterAdvanced,
  useATFilter,
  useATSelection,
  useATisActive,
  useATPageIndex,
  useATPageSize,
  ATColumnsCtxType,
  ATFilterAdvancedCtxType,
  ATFilterCtxType,
  ATSelectionCtxType,
  ATisActiveCtxType,
  ATPageIndexCtxType,
  ATPageSizeCtxType,
  ATColumnsContextProvider,
  ATFilterAdvancedContextProvider,
  ATFilterContextProvider,
  ATSelectionContextProvider,
  ATisActiveContextProvider,
  ATPageContextProvider,
} from './hooks';
import type {
  ATProps,
  ATFilterBarProps,
  ATRenderCellProps,
  ATRenderRowProps,
  ATColumnHeaderType,
  ATColumnsConfigType,
  ATFilterConfigItemType,
  ATFilterConfigComponentType,
} from './actionTable/ATTypes';
import { processFilters } from './filter/FilterLogic';

export interface ActionTableComponent {
  <
    T extends Record<string, any>,
    TExtra extends Record<string, any> = {},
    TContext = any,
  >(
    props: ATProps<T, TExtra, TContext>,
  ): JSX.Element;
  Provider: React.FC<ATContextProviderProps>;
  ColumnsProvider: React.FC<ATContextProviderProps>;
  FilterProvider: React.FC<ATContextProviderProps>;
  FilterAdvancedProvider: React.FC<ATContextProviderProps>;
  SelectionProvider: React.FC<ATContextProviderProps>;
  isActiveProvider: React.FC<ATContextProviderProps>;
  PageProvider: React.FC<ATContextProviderProps>;
  FilterBar: <T extends Record<string, any>>({
    header,
    filterSelect,
    filterCheckbox,
  }: ATFilterBarProps<T>) => JSX.Element;

  useColumns: () => ATColumnsCtxType<any>;
  useFilter: () => ATFilterCtxType<any>;
  useFilterAdvanced: () => ATFilterAdvancedCtxType<any>;
  useSelection: () => ATSelectionCtxType<any>;
  useIsActive: () => ATisActiveCtxType;
  usePageIndex: () => ATPageIndexCtxType;
  usePageSize: () => ATPageSizeCtxType;
  processFilters: <T extends Record<string, any>>(
    items: T[],
    filterValue: string,
    filterCols: ATColumnType<T>[],
    filtersAdvanced: ATFilterItemsType<T>,
  ) => T[];
}

const _ActionTable = ActionTableMain as unknown as ActionTableComponent;
_ActionTable.FilterBar = ActionTableFilterBar;
_ActionTable.Provider = ATContextProvider;
_ActionTable.ColumnsProvider = ATColumnsContextProvider;
_ActionTable.FilterProvider = ATFilterContextProvider;
_ActionTable.FilterAdvancedProvider = ATFilterAdvancedContextProvider;
_ActionTable.SelectionProvider = ATSelectionContextProvider;
_ActionTable.isActiveProvider = ATisActiveContextProvider;
_ActionTable.PageProvider = ATPageContextProvider;
_ActionTable.useColumns = useATColumns;
_ActionTable.useFilter = useATFilter;
_ActionTable.useFilterAdvanced = useATFilterAdvanced;
_ActionTable.useSelection = useATSelection;
_ActionTable.useIsActive = useATisActive;
_ActionTable.usePageIndex = useATPageIndex;
_ActionTable.usePageSize = useATPageSize;
_ActionTable.processFilters = processFilters;

export const ActionTable = _ActionTable;

export namespace ActionTable {
  export type HeaderType<
    T,
    TExtra extends Record<string, any> = {},
  > = ATColumnHeaderType<T & TExtra>;
  export type RenderCellType<
    T extends Record<string, any>,
    TExtra extends Record<string, any> = {},
    TContext = any,
  > = ATRenderCellProps<T & TExtra, {}, TContext>;
  export type RenderRowType<
    T extends Record<string, any>,
    TExtra extends Record<string, any> = {},
    TContext = any,
  > = ATRenderRowProps<T & TExtra, {}, TContext>;
  export type ColumnType<T> = ATColumnType<T>;
  export type ColumnConfigType<T extends Record<string, any>> =
    ATColumnConfigType<T>;
  export type ColumnConfigOptionsType = ATColumnConfigOptionsType;
  export type ColumnsConfigType<
    T extends Record<string, any>,
    TExtra extends Record<string, any> = {},
  > = ATColumnsConfigType<T & TExtra>;
  export type FilterConfigItemType<T extends Record<string, any>> =
    ATFilterConfigItemType<T>;
  export type FilterConfigComponentType<T extends Record<string, any>> =
    ATFilterConfigComponentType<T>;
}
